export const PROTOCOL_VERSION = 29;
export const CONTENT_VERSION = "phase4.30-world-threat-readability";
export const MAX_PLAYERS = 4;
export const RECONNECT_WINDOW_MS = 90_000;
export const DEFAULT_GAME_DURATION_MS = 21 * 60_000 + 30_000;
export const WORLD_WIDTH = 12_000;
export const WORLD_HEIGHT = 8_000;
export const PLAYER_LEVEL_CAP = 20;
export const PLAYER_TALENT_POINT_CAP = 20;
export const PLAYER_ABILITY_SLOTS = 6;

export type RoomPhase = "lobby" | "playing" | "victory" | "draw" | "defeat";
export type DifficultyId = "apprentice" | "veteran" | "master";
export type EnemyRank = "normal" | "elite" | "boss";
export type ModuleTier = 1 | 2 | 3 | 4 | 5 | 6;
export type LaneId = "north" | "east" | "south" | "west";
export type PlayerStatus = "active" | "downed" | "lich";
/** @deprecated Protocol-18 mining request compatibility only. */
export type MineCaveId = 1 | 2 | 3 | 4 | 5;
export type WorldSpaceId = "surface";
export type TeamSignalKind = "danger" | "assist" | "objective";
export type SideObjectiveId = "moonwell" | "salvage" | "rift";
export type TeamTechId =
  | "core_vitality"
  | "core_armor"
  | "war_chant"
  | "haste_chant"
  | "familiar_fury"
  | "familiar_ward"
  | "guardian_oath"
  | "tower_power"
  | "tower_haste"
  | "tower_ward"
  | "tower_vitality"
  | "wall_vitality"
  | "wall_armor";
export type TalentId =
  | "vital_essence"
  | "mana_well"
  | "life_font"
  | "mana_tide"
  | "war_essence"
  | "haste_essence"
  | "swift_essence"
  | "stone_skin"
  /** @deprecated Protocol-18 compatibility only; mining survey is archived. */
  | "survey"
  | "spirit_sight"
  | "far_sight";

export type AbilityCategory =
  | "attack"
  | "area"
  | "elite_attack"
  | "labor"
  | "finance"
  | "pasture"
  | "mining"
  /** @deprecated Archived content compatibility only. */
  | "archaeology"
  | "enhancement"
  | "follower"
  | "summon"
  | "healing"
  | "growth"
  | "one_shot"
  | "guard"
  | "special";

export type TargetMode =
  | "random"
  | "closest"
  | "farthest"
  | "highest_health"
  | "lowest_health"
  | "closest_to_core"
  | "fastest"
  | "alternating_extremes"
  | "random_area"
  | "elite_only"
  | "boss_only";

export type CompanionKind = "pasture" | "follower" | "summon" | "guard";
export type AbilityEntityKind = CompanionKind | "field";
export type EarthSpiritStar = 0 | 1 | 2 | 3 | 4 | 5;
export type EarthSpiritBehavior =
  | "alert"
  | "attacking"
  | "waiting"
  | "returning"
  | "aggregating";
export type DamageSourceKind =
  | "body"
  | "ability"
  | "hidden_effect"
  | CompanionKind;

export interface DamageSourceEntry {
  key: string;
  kind: DamageSourceKind;
  abilityId: string | null;
  hiddenSourceId?: string | null;
  damage: number;
}

export type GrowthEvent =
  | "second"
  | "move_distance"
  | "attack"
  | "damage_taken"
  | "kill"
  | "repair"
  /** @deprecated Archived content compatibility only. */
  | "excavate"
  | "companion_income";
export type LaborEvent =
  | "move_distance"
  | "attack"
  | "repair"
  /** @deprecated Archived content compatibility only. */
  | "excavate";

export interface Vec2 {
  x: number;
  y: number;
}

export interface StatBonuses {
  damageAdd?: number;
  damageMultiplier?: number;
  attackDamageBonus?: number;
  abilityDamageBonus?: number;
  baseAttackSpeedModifier?: number;
  attackSpeedBonus?: number;
  fireIntervalMultiplier?: number;
  rangeAdd?: number;
  moveSpeedAdd?: number;
  moveSpeedMultiplier?: number;
  maxHealthAdd?: number;
  baseMaxHealthRatio?: number;
  armorAdd?: number;
  dodgeAdd?: number;
  goldMultiplier?: number;
  scrapMultiplier?: number;
  experienceMultiplier?: number;
  repairPerSecond?: number;
  healingPerSecond?: number;
  healthRegenAdd?: number;
  healthRegenMaxRatio?: number;
  manaMaxAdd?: number;
  manaMaxMultiplier?: number;
  manaRegenAdd?: number;
  manaRegenMaxRatio?: number;
  scoutingRadiusAdd?: number;
  splashRadius?: number;
  chainTargets?: number;
  pierceTargets?: number;
  crystalDropChance?: number;
  coreDropChance?: number;
  eliteDamageMultiplier?: number;
  companionDamageMultiplier?: number;
  summonCapAdd?: number;
  /** @deprecated Protocol-18 mining compatibility only. */
  surveyRadiusAdd?: number;
  /** General anti-invisibility radius retained from the pre-mining build. */
  revealRadius?: number;
  /** @deprecated Archived archaeology compatibility only. */
  archaeologyMultiplier?: number;
  /** @deprecated Archived archaeology compatibility only. */
  wageShareAdd?: number;
}

export interface CombatDefinition {
  damage: number;
  intervalMs: number;
  range: number;
  target: TargetMode;
  radius?: number;
  areaDamageMultiplier?: number;
  projectiles?: number;
  chainTargets?: number;
  pierceTargets?: number;
  slowRatio?: number;
  slowDurationMs?: number;
  slowBosses?: boolean;
  stickyTarget?: boolean;
  stunDurationMs?: number;
  stunBosses?: boolean;
  manaCost?: number;
  executeNormalEveryMs?: number;
  /** Repeats the authored hit on the same selected target. */
  hits?: number;
  /** Places radius damage around the caster instead of the selected enemy. */
  areaOrigin?: "target" | "caster";
  pullDistance?: number;
  knockbackDistance?: number;
  armorReduction?: {
    amount: number;
    durationMs: number;
  };
  rootEveryHits?: {
    hits: number;
    durationMs: number;
    bossesImmune: boolean;
  };
  delayedEcho?: {
    delayMs: number;
    damage: number;
    target: "nearest_other" | "same_target";
  };
  /** Repeats this authored cast on each Nth release (for example, every fifth cast). */
  doubleEveryAttacks?: number;
  /** Restores the owner when this combat definition directly secures a kill. */
  healOnKill?: number;
}

export interface EconomyDefinition {
  trigger: "periodic" | LaborEvent;
  intervalMs?: number;
  distance?: number;
  gold?: number;
  experience?: number;
  crystalChance?: number;
  condition?: "alone" | "near_ally" | "mana_above_half";
}

export interface CompanionDefinition {
  kind: CompanionKind;
  presentationId: string;
  count: number;
  orbitRadius: number;
  moveSpeed: number;
  damage?: number;
  intervalMs?: number;
  range?: number;
  health?: number;
  armor?: number;
  manaCost?: number;
  spawnIntervalMs?: number;
  respawnMs?: number;
  incomeIntervalMs?: number;
  incomeGold?: number;
  ethereal?: boolean;
  /** Summons with this flag are intentionally permanent and uncapped. */
  ignoreSummonCap?: boolean;
  taunt?: boolean;
  targetPriority?:
    | "random"
    | "closest"
    | "highest_health"
    | "lowest_health"
    | "closest_to_core"
    | "fastest"
    | "alternating_extremes"
    | "last_player_skill_target";
  onHit?: {
    revealDurationMs?: number;
    pullDistance?: number;
    slowRatio?: number;
    slowDurationMs?: number;
    forcedRetreatDistance?: number;
    forcedRetreatSlowRatio?: number;
  };
  deathBurst?: {
    radius: number;
    damage: number;
  };
  periodicHeal?: {
    intervalMs: number;
    range: number;
    amount: number;
    target: "self_and_lowest_guard" | "lowest_ally";
  };
  ownerAura?: {
    range: number;
    abilityDamageBonus: number;
  };
  movement?:
    | {
        kind: "player_centered_wander";
        wanderRadius: number;
        returnTriggerRadius: number;
        returnReleaseRadius: number;
        wanderSpeedRatio: number;
        returnSpeedRatio: number;
      }
    | {
        kind: "player_centered_random";
        radius: number;
        speedBaseRatio: number;
      };
}

export interface ExperienceVaultEvolutionBranch {
  targetModuleId: string;
  weight: number;
}

export interface ExperienceVaultDefinition {
  capacity: number;
  replacementPayoutMultiplier: number;
  suppressPayoutOnEvolution: true;
  evolution:
    | {
        kind: "fixed";
        targetModuleId: string;
      }
    | {
        kind: "weighted";
        branches: ExperienceVaultEvolutionBranch[];
      };
}

export interface KillChargeDefinition {
  trigger: "direct_kill";
  requiredCount: number;
  targetModuleId: string;
  sameSlot: true;
}

export type FinanceResourceId = "gold" | "crystal";

export type FinanceStakeTargetId =
  | "current_health"
  | "max_health"
  | "health_regen"
  | "health_regen_ratio"
  | "current_mana"
  | "max_mana"
  | "mana_regen"
  | "mana_regen_ratio"
  | "attack_damage"
  | "damage_multiplier"
  | "attack_damage_multiplier"
  | "ability_damage_multiplier"
  | "base_attack_speed"
  | "bonus_attack_speed"
  | "attack_range"
  | "base_move_speed"
  | "final_move_speed"
  | "move_speed_multiplier"
  | "armor"
  | "dodge"
  | "repair_per_second"
  | "healing_per_second"
  | "scouting_radius"
  | "reveal_radius"
  | "splash_radius"
  | "chain_targets"
  | "pierce_targets"
  | "crystal_drop_chance"
  | "elite_damage_multiplier"
  | "companion_damage_multiplier"
  | "summon_cap"
  | "gold_multiplier"
  | "experience_multiplier"
  | "surface_mining_extra_gold_flat"
  | "surface_mining_extra_gold_multiplier"
  | "surface_mining_speed_bonus"
  | "surface_mining_gold_yield_bonus"
  | "surface_mining_crystal_yield_ratio"
  | "gold"
  | "crystal"
  | "experience"
  | "talent_points"
  | "wage";

export interface FinanceOutcomeBranchDefinition {
  weight: number;
  targetModuleId: string;
  reward?: {
    resource: FinanceResourceId;
    amount: number;
  };
}

export type FinanceTimedOutcomeDefinition =
  | {
      kind: "weighted_branches";
      delayMs: number;
      branches: FinanceOutcomeBranchDefinition[];
    }
  | {
      kind: "stake_resolution";
      delayMs: number;
      fixedSuccessPercent?: number;
      dynamicSuccess?: {
        basePercent: number;
        repeatableHiddenEffectId: string;
        percentPerCount: number;
        maxPercent: number;
      };
      stakeReturnMultiplier: number;
      successGrant?: {
        repeatableHiddenEffectId: string;
        count: number;
      };
      returnTargetModuleId: string;
    };

export interface FinanceStateDefinition {
  familyId: string;
  timeScaledIncome?: {
    intervalMs: number;
    elapsedUnitMs: number;
    resource: FinanceResourceId;
    round: "ceil";
  };
  resourceGate?: {
    resource: FinanceResourceId;
    cost: number;
    targetModuleId: string;
  };
  timedOutcome?: FinanceTimedOutcomeDefinition;
  onEnterGrant?: {
    resources?: Array<{
      resource: FinanceResourceId;
      amount: number;
    }>;
    repeatableHiddenEffectId?: string;
    count?: number;
  };
  timedTransition?: {
    delayMs: number;
    targetModuleId: string;
  };
  periodicDrain?: {
    intervalMs: number;
    resource: FinanceResourceId;
    amount: number;
    floor: number;
  };
  periodicSpendAccumulator?: {
    intervalMs: number;
    resource: FinanceResourceId;
    costPerTick: number;
    threshold: number;
    thresholdReward: {
      resource: FinanceResourceId;
      minInclusive: number;
      maxInclusive: number;
      integer: true;
    };
    rareTransition: {
      successPercent: number;
      targetModuleId: string;
    };
    resetOnFailure: true;
  };
  periodicCharge?: {
    intervalMs: number;
    amountPerTick: number;
    threshold: number;
    targetModuleId: string;
  };
  onReplacementReward?: {
    resource: FinanceResourceId;
    amount: number;
  };
  randomStake?: {
    ratio: number;
    targetPool: FinanceStakeTargetId[];
    targetModuleId: string;
  };
}

export type LaborDefinition =
  | {
      kind: "movement_distance";
      payoutDistance: number;
      goldPerPayout: number;
      achievementDistance: number;
      hiddenEffectId: string;
    }
  | {
      kind: "circle_traversal";
      sampleIntervalMs: number;
      minSegmentDistance: number;
      minPathLength: number;
      minSpanX: number;
      minSpanY: number;
      closeDistance: number;
      minAbsoluteTurnRadians: number;
      idleResetMs: number;
      goldPerCompletion: number;
      achievementCount: number;
      hiddenEffectId: string;
    }
  | {
      kind: "stationary_work";
      payoutIntervalMs: number;
      goldPerPayout: number;
      movingDrainIntervalMs: number;
      movingDrainGold: number;
      achievementStationaryMs: number;
      hiddenEffectId: string;
    }
  | {
      kind: "route_leg";
      route: "core_outer_gate";
      endpointRadius: number;
      goldPerLeg: number;
      achievementLegs: number;
      hiddenEffectId: string;
    }
  | {
      kind: "kill_bonus";
      goldPerOwnedKill: number;
      achievementBonusGold: number;
      hiddenEffectId: string;
    }
  | {
      kind: "gate_to_gate_patrol";
      endpointRadius: number;
      goldPerPatrol: number;
    }
  | {
      kind: "repair_payout";
      repairPerPayout: number;
      goldPerPayout: number;
    }
  | {
      kind: "team_repair_payout";
      allyRadius: number;
      repairPerPayout: number;
      goldPerPayout: number;
      ownRepairMultiplier: number;
    };

export interface ModuleStateFamilyDefinition {
  familyId: string;
  canonicalModuleId: string;
  inheritsAscension?: true;
  inheritsSlotLock?: true;
}

// Shared unit templates are content data. Keeping their IDs open-ended lets the
// local workshop add new templates without requiring a TypeScript source edit.
export type UndeadUnitId = string;

export type UndeadUnitAbilityDefinition =
  | {
      kind: "taunt";
    }
  | {
      kind: "cone_splash";
      angleDegrees: number;
      radius: number;
      damageMultiplier: number;
    }
  | {
      kind: "charge";
      intervalMs: number;
      acquireRange: number;
      pathWidth: number;
      damage: number;
      stunDurationMs: number;
      stunBosses: boolean;
    }
  | {
      kind: "kill_heal";
      healing: number;
    }
  | {
      kind: "periodic_area_damage";
      intervalMs: number;
      radius: number;
      damage: number;
    }
  | {
      kind: "kill_growth";
      maxHealthAdd: number;
      currentHealthAdd: number;
      damageAdd: number;
    };

export interface UndeadUnitDefinition {
  id: UndeadUnitId;
  name: string;
  presentationId: string;
  health: number;
  armor: number;
  damage: number;
  intervalMs: number;
  range: number;
  moveSpeedBaseRatio: number;
  abilities: UndeadUnitAbilityDefinition[];
}

export interface WeightedUndeadEntry {
  unitId: UndeadUnitId;
  weight: number;
}

export type GuardFormId =
  | "armor_golem"
  | "heroic_soul"
  | "oathbound_guard"
  | "divine_agent";

interface GrowthDefinitionBase {
  event: GrowthEvent;
  every: number;
  cap?: number;
  /** Lowers the next settlement threshold after every completed settlement. */
  everyReductionPerSettlement?: number;
  /** Floor applied after everyReductionPerSettlement. */
  minEvery?: number;
}

export type GrowthDefinition = GrowthDefinitionBase &
  (
    | {
        stat: "damage" | "maxHealth" | "armor" | "moveSpeed" | "manaMax";
        amount: number;
        bonuses?: never;
      }
    | {
        stat?: never;
        amount?: never;
        bonuses: Partial<
          Record<
            "damage" | "maxHealth" | "armor" | "moveSpeed" | "manaMax",
            number
          >
        >;
      }
  );

export interface OneShotDefinition {
  gold?: number;
  experience?: number;
  crystals?: number;
  damage?: number;
  maxHealth?: number;
  maxMana?: number;
  armor?: number;
  attackSpeedBonus?: number;
  abilityDamageBonus?: number;
  manaRegenMaxRatio?: number;
  currentLevelExperienceRatio?: number;
  grantLevels?: number;
  repeatableHiddenEffectId?: string;
  talentPoints?: number;
}

export interface HealingDefinition {
  target: "self" | "team" | "core" | "lowest_ally";
  amount: number;
  intervalMs: number;
  range?: number;
}

export type AbilityMechanicDefinition =
  | {
      kind: "death_guard";
      cooldownMs: number;
      invulnerabilityMs: number;
      recoveryDurationMs: number;
      recoveryHealthRatio: number;
    }
  | {
      kind: "periodic_target_damage";
      intervalMs: number;
      range: number;
      damage: number;
      target: "random" | "heavy";
      hits?: number;
      slotIndependent?: boolean;
    }
  | {
      kind: "companion_aura";
      range: number;
      statMultiplier: number;
      targets: Array<Extract<CompanionKind, "summon" | "guard">>;
    }
  | {
      kind: "periodic_reroll";
      intervalMs: number;
      excludeSelf: boolean;
      preserveTier: true;
    }
  | {
      kind: "random_skill_cast";
      intervalMs: number;
      tiers: ModuleTier[];
      categories?: AbilityCategory[];
    }
  | {
      kind: "enemy_conversion";
      intervalMs: number;
      range: number;
      targetRank: "normal";
      statMultiplier: number;
    }
  | {
      kind: "black_hole";
      spawnIntervalMs: number;
      castRange: number;
      diameter: number;
      durationMs: number;
      tickIntervalMs: number;
      damage: number;
    }
  | {
      kind: "basic_attack_cannon";
      priority: number;
      maxManaCostRatio: number;
      baseDiameter: number;
      secondaryDamagePerMana: number;
      diameterMultiplierPerManaRatio: number;
      baseAttackSpeedModifier: number;
    }
  | {
      kind: "pasture_effect_amplifier";
      effectMultiplier: number;
    }
  | {
      kind: "undead_summon";
      unitId: UndeadUnitId;
      intervalMs: number;
      manaCost: number;
    }
  | {
      kind: "lich_form";
      mode: "temporary_on_death" | "permanent";
      undeadHealthDamageMultiplier: number;
      undeadSummonIntervalMultiplier: number;
      immediateBaseRespawn: boolean;
    }
  | {
      kind: "guard_periodic_area_damage";
      intervalMs: number;
      radius: number;
      damage: number;
    }
  | {
      kind: "earth_spirit_summon";
      spawnRadius: number;
      alertRange: number;
      returnDelayMs: number;
    }
  | {
      kind: "follower_periodic_damage";
      intervalMs: number;
      range: number;
      damage: number;
      hits: number;
      consumeEmptyTick?: boolean;
    }
  | {
      kind: "periodic_self_area_damage";
      intervalMs: number;
      radius: number;
      damage: number;
      slowRatio?: number;
      slowDurationMs?: number;
      healOnPositiveHit?: number;
      slotIndependent?: boolean;
    }
  | {
      kind: "periodic_normal_execute_reward";
      intervalMs: number;
      range: number;
      rewardMultiplier: number;
    }
  | {
      kind: "periodic_forced_aggro";
      intervalMs: number;
      range: number;
      slowRatio: number;
      targetRank: "non_boss";
    }
  | {
      kind: "ability_kill_growth";
      killsPerSettlement: number;
      maxHealthAdd: number;
      damageAdd: number;
    }
  | {
      kind: "damage_nullification";
      thresholdMaxHealthRatio: number;
      cooldownMs: number;
      strictGreaterThan: true;
    }
  | {
      kind: "basic_attack_chain";
      initialDamage: number;
      targets: number;
      damageLossPerBounce: number;
      bounceRange: number;
    }
  | {
      kind: "movement_damage_field";
      distancePerField: number;
      radius: number;
      durationMs: number;
      tickIntervalMs: number;
      damage: number;
      suppressOverlap: boolean;
    }
  | {
      kind: "guard_charge";
      intervalMs: number;
      acquireRange: number;
      pathWidth: number;
      damage: number;
    }
  | {
      kind: "follower_periodic_area_damage";
      intervalMs: number;
      radius: number;
      damage: number;
      slowRatio?: number;
      slowDurationMs?: number;
    }
  | {
      kind: "periodic_health_extremes_damage";
      intervalMs: number;
      range: number;
      highestDamage: number;
      lowestDamage: number;
    }
  | {
      kind: "periodic_distance_extremes_damage";
      intervalMs: number;
      range: number;
      damage: number;
    }
  | {
      kind: "basic_attack_health_cost";
      maxHealthCostRatio: number;
      bonusDamage: number;
      cannotKillOwner: true;
      healOnKillFlat?: number;
      healOnKillMaxHealthRatio?: {
        normal: number;
        elite: number;
        boss: number;
      };
    }
  | {
      kind: "guard_periodic_strike";
      intervalMs: number;
      range: number;
      damage: number;
      selfHealing?: number;
    }
  | {
      kind: "guard_basic_attack_splash";
      radius: number;
      damageMultiplier: number;
    }
  | {
      kind: "summon_damage_aura";
      intervalMs: number;
      radius: number;
      damage: number;
    }
  | {
      kind: "every_nth_summon_commander";
      every: number;
      presentationId: string;
      health: number;
      armor: number;
      damage: number;
      intervalMs: number;
      range: number;
      moveSpeedBaseRatio: number;
      auraRadius: number;
      auraAttackSpeedBonus: number;
      auraMoveSpeedBonus: number;
      auraTargetAbilityIds: string[];
    }
  | {
      kind: "boss_attack_delay_on_hit";
      delayMs: number;
    }
  | {
      kind: "gold_contract";
      drainIntervalMs: number;
      drainMode: "flat" | "current_ratio";
      drainValue: number;
      fixedDamageAdd?: number;
      fixedArmorAdd?: number;
      damageEqualsCurrentGold?: true;
    }
  | {
      kind: "death_growth";
      maxHealthAdd: number;
      damageAdd: number;
      respawnDelayAddMs: number;
    }
  | {
      kind: "energy_reservoir";
      chargePerSecond: number;
      range: number;
      triggerBelowRatio: number;
      healthPerEnergy: number;
      manaPerEnergy: number;
      instant: true;
    }
  | {
      kind: "damage_lifesteal";
      intervalMs: number;
      damageRatio: number;
      healCap: number;
    }
  | {
      kind: "cooldown_acceleration";
      intervalMs: number;
      advanceMs: number;
      includeTierSix: boolean;
      excludeSelf: true;
    }
  | {
      kind: "synergy_substitution";
      intervalMs: number;
      durationMs: number;
      allowTierSixModule: boolean;
      allowSynergyRequirement: false;
      allowHiddenSkill: false;
    };

export interface StagedAscensionDefinition {
  targetModuleId: string;
  costMultiplier: number;
}

export interface ArchaeologyDefinition {
  revealRadius?: number;
  yieldMultiplier?: number;
  wageShare?: number;
  chestKey?: "crimson" | "azure" | "verdant" | "golden";
}

/**
 * P4.13 lightweight surface-mining skill tuning. This is intentionally
 * separate from the archived protocol-18 `mining` permission model.
 */
export interface SurfaceMiningDefinition {
  schemaVersion: 1 | 2;
  /** Additive settlement-rate bonus. 0.5 means 50% faster. */
  speedBonus?: number;
  /** Additive bonus to final ordinary mining gold. 0.5 means +50%. */
  goldYieldBonus?: number;
  /** Multiplies an already-positive non-self reveal radius; never grants reveal. */
  conditionalRevealRadiusBonus?: number;
  /** Ordinary-mining crystal yield as a ratio of final paid gold. */
  crystalYieldRatio?: number;
  /** Speed that is available only while the settlement can pay its mana cost. */
  manaPoweredSpeed?: {
    speedBonus: number;
    manaCostPerSettlement: number;
  };
  /** Hidden Big-Bag vein-devour behavior; excluded from normal mining bonuses. */
  devour?: {
    channelMs: number;
    cooldownMs: number;
    goldPerRemaining: number;
    moveSpeedPenaltyPerSuccess: number;
  };
  /** Hidden Weizhen contact behavior; excluded from normal mining bonuses. */
  contactAttack?: {
    fixedBaseMoveSpeedRatio: number;
    damage: number;
    enemyCooldownMs: number;
    knockbackDistance: number;
    healOnKill: number;
  };
  /** @deprecated P4.13 override compatibility. Prefer additive speedBonus. */
  /** Multiplies the personal settlement interval; values below one are faster. */
  settlementIntervalMultiplier?: number;
  /** Extra integer gold for a full settlement, scaled on a partial final pull. */
  extraGoldFlat?: number;
  /** Extra gold proportional to the actual base amount extracted. */
  extraGoldMultiplier?: number;
  /** Chance to duplicate the actual settlement payout without depleting more ore. */
  replicationChance?: number;
  /** Adds to the existing general anti-invisibility radius. */
  revealRadiusAdd?: number;
  /** Adds to the existing general scouting radius. */
  scoutingRadiusAdd?: number;
  /** Reserved identifiers for future advanced discovery mechanics. */
  advancedDiscoveryIds?: string[];
}

export type ConditionalEvolutionDefinition =
  | {
      kind: "surface_mining_devour_count";
      requiredCount: number;
      targetModuleId: string;
      sameSlot: true;
    }
  | {
      kind: "player_level";
      requiredLevel: number;
      targetModuleId: string;
      sameSlot: true;
    };

export type SpecialBehaviorId =
  | "pasture_inherits_attack"
  | "portal_on_hit"
  | "copy_random_ability"
  | "emergency_overdrive"
  | "companion_rebirth_burst"
  | "all_chest_keys"
  | "summon_echoes";

export interface ModuleDefinition {
  id: string;
  name: string;
  description: string;
  tier: ModuleTier;
  category: AbilityCategory;
  tags: string[];
  weight: number;
  hidden?: boolean;
  bonuses: StatBonuses;
  combat?: CombatDefinition;
  economy?: EconomyDefinition;
  companion?: CompanionDefinition;
  growth?: GrowthDefinition;
  onAcquire?: OneShotDefinition;
  experienceVault?: ExperienceVaultDefinition;
  killCharge?: KillChargeDefinition;
  financeState?: FinanceStateDefinition;
  labor?: LaborDefinition;
  stateFamily?: ModuleStateFamilyDefinition;
  healing?: HealingDefinition;
  /** @deprecated Archived content compatibility only. */
  archaeology?: ArchaeologyDefinition;
  surfaceMining?: SurfaceMiningDefinition;
  mechanics?: AbilityMechanicDefinition[];
  grantsHiddenEffectIds?: string[];
  special?: SpecialBehaviorId;
  conditionalEvolution?: ConditionalEvolutionDefinition;
  stagedAscension?: StagedAscensionDefinition;
  evolutionTo?: string;
  evolutionFrom?: string;
}

export type AbilityDefinition = ModuleDefinition;

export type HiddenRuntimeEffect =
  | {
      kind: "periodic_area_damage";
      radius: number;
      intervalMs: number;
      damage: number;
    }
  | {
      kind: "ally_healing_aura";
      radius: number;
      healingPerSecond: number;
    }
  | {
      kind: "periodic_heavy_damage";
      radius: number;
      intervalMs: number;
      damage: number;
    }
  | {
      kind: "ally_armor_aura";
      radius: number;
      armor: number;
    }
  | {
      kind: "ally_speed_aura";
      radius: number;
      attackSpeedMultiplier: number;
      moveSpeedMultiplier: number;
    }
  | {
      kind: "periodic_normal_execute";
      radius: number;
      intervalMs: number;
    }
  | {
      kind: "low_health_execute";
      radius: number;
      healthRatio: number;
    }
  | {
      kind: "periodic_random_damage";
      radius: number;
      intervalMs: number;
      damage: number;
    }
  | {
      kind: "enemy_slow_immunity";
    }
  | {
      kind: "periodic_non_boss_push_damage";
      radius: number;
      intervalMs: number;
      damage: number;
      pushOutsideRadius: number;
    }
  | {
      kind: "base_move_speed_multiplier";
      multiplier: number;
    }
  | {
      kind: "normal_enemy_taunt_aura";
      radius: number;
      priority: "standard";
    }
  | {
      kind: "every_nth_cast_double";
      every: number;
    };

export interface HiddenEffectDefinition {
  id: string;
  name: string;
  description: string;
  startsLocked: boolean;
  repeatable?: {
    trackSourceTier: boolean;
  };
  effects: HiddenRuntimeEffect[];
}

export interface RepeatableHiddenRequirement {
  effectId: string;
  count: number;
  afterSynergyId?: string;
  minSourceTier?: ModuleTier;
  exactSourceTier?: ModuleTier;
  activationBoundary: "after_prerequisite";
  activationGrantCarriesForward: false;
}

export interface HiddenSynergyDefinition {
  id: string;
  name: string;
  description: string;
  requiredEffectIds: string[];
  activatesEffectIds: string[];
  repeatableRequirement?: RepeatableHiddenRequirement;
  suppressesSynergyIds?: string[];
  effects: HiddenRuntimeEffect[];
}

export interface HiddenEffectState {
  id: string;
  name: string;
  description: string;
  active: boolean;
}

export interface HiddenSynergyState {
  id: string;
  name: string;
  description: string;
}

export interface RepeatableHiddenEffectState {
  id: string;
  name: string;
  description: string;
  totalCount: number;
  sourceTierCounts: Record<ModuleTier, number>;
}

export interface HiddenSynergyProgressState {
  id: string;
  name: string;
  description: string;
  active: boolean;
  progress: number;
  requiredCount: number;
  eligibleTierLabel: string;
}

export type SynergyRequirement =
  | { kind: "module"; moduleId: string }
  | { kind: "synergy"; synergyId: string }
  | { kind: "one_of"; moduleIds: string[] }
  | { kind: "tag_count"; tag: string; count: number }
  | { kind: "tier_count"; minTier: ModuleTier; count: number };

export type SynergyMechanicDefinition =
  | {
      kind: "stationary_stacks";
      initialDelayMs: number;
      stackIntervalMs: number;
      stackDurationMs: number;
      refreshAllOnStack: true;
      maxStacks: number;
      attackSpeedBonusPerStack: number;
      manaRegenMaxRatioPerStack: number;
      abilityDamageBonusPerStack: number;
    }
  | {
      kind: "turret_transformation";
      movementStepMs: number;
      movingBaseSpeedGainRatio: number;
      stationaryBaseSpeedLossRatio: number;
      maxBaseMoveSpeedRatio: number;
      deployedRangeMultiplier: number;
      deployedDamageMultiplier: number;
      deployedBaseAttackSpeedModifier: number;
      deployedMaxHealthAdd: number;
      deployedArmorAdd: number;
      tauntRadius: number;
    }
  | {
      kind: "pasture_hunt";
      intervalMs: number;
      range: number;
      damagePerTotalTier: number;
    }
  | {
      kind: "hidden_effect_amplifier";
      hiddenEffectIds: string[];
      effectMultiplier: number;
    }
  | {
      kind: "periodic_heavy_damage";
      intervalMs: number;
      range: number;
      damage: number;
    }
  | {
      kind: "undead_catastrophe";
      triggerAbilityId: string;
      summonChance: number;
      normalPool: WeightedUndeadEntry[];
      heavyPool: WeightedUndeadEntry[];
    }
  | {
      kind: "guard_critical";
      targetAbilityId: string;
      damageAdd: number;
      attacksPerCritical: number;
      criticalDamageMultiplier: number;
    }
  | {
      kind: "guard_dash";
      targetAbilityId: string;
      attackSpeedBonus: number;
      moveSpeedBonus: number;
      intervalMs: number;
      acquireRange: number;
      pathWidth: number;
      damage: number;
      knockbackBosses: boolean;
    }
  | {
      kind: "guard_oath_upgrade";
      targetAbilityId: string;
      consumedAbilityId: string;
      health: number;
      damage: number;
      armor: number;
      intervalMs: number;
      range: number;
      moveSpeedBaseRatio: number;
      respawnMs: number;
    }
  | {
      kind: "divine_guard_upgrade";
      targetAbilityId: string;
      suppressedModuleIds: string[];
      positiveStatMultiplier: number;
      healthAdd: number;
      damageAdd: number;
      armorAdd: number;
      attackSpeedBonus: number;
      moveSpeedBonus: number;
      abilityDamageBonus: number;
      range: number;
      respawnMs: number;
      holyFireIntervalMs: number;
      holyFireRadius: number;
      holyFireDamage: number;
      judgmentAttacksPerCritical: number;
      judgmentDamageMultiplier: number;
      descentIntervalMs: number;
      descentAcquireRange: number;
      descentRadius: number;
      descentDamage: number;
      descentPreferHeavy: boolean;
      descentKnockbackBosses: boolean;
    }
  | {
      kind: "earth_spirit_aggregation";
      targetAbilityId: string;
      basePresentationId: string;
      rockPresentationIdPrefix: string;
      aggregationRange: number;
      participantsRequired: number;
      maxStar: Extract<EarthSpiritStar, 5>;
      baseHealth: number;
      baseArmor: number;
      baseDamage: number;
      baseIntervalMs: number;
      baseRange: number;
      baseMoveSpeedRatio: number;
      healthMultiplierPerStar: number;
      armorMultiplierPerStar: number;
      damageMultiplierPerStar: number;
      healthRoundUpTo: number;
      armorRoundUpTo: number;
      damageRoundUpTo: number;
      rulerAuraRadius: number;
      rulerAttackSpeedBonus: number;
    }
  | {
      kind: "follower_granted_skill";
      targetAbilityId: string;
      grantedAbilityId: string;
    }
  | {
      kind: "day_night_cycle";
      dayDurationMs: number;
      nightDurationMs: number;
      range: number;
      dayIntervalMs: number;
      dayDamage: number;
      nightIntervalMs: number;
      nightDamage: number;
    }
  | {
      kind: "greed_paradise";
      forcedAggroAbilityId: string;
      growthAbilityId: string;
      forcedDamageIntervalMs: number;
      forcedDamage: number;
      killsPerDamageGrowth: number;
      damagePerSettlement: number;
    }
  | {
      kind: "surface_mining_skill_amplifier";
      positiveEffectMultiplier: number;
      excludedModuleIds: string[];
    }
  | {
      kind: "material_guard_upgrade";
      targetAbilityId: string;
      consumedAbilityId: string;
      healthAdd: number;
      armorAdd: number;
      damageAdd: number;
      respawnMs: number;
    }
  | {
      kind: "paper_units";
      abilityIds: string[];
      attackSpeedBonus: number;
      doubleEveryAttacks: number;
    }
  | {
      kind: "mining_exhaustion_bonus";
      goldRatioOfInitialReserve: number;
    }
  | {
      kind: "chaotic_mess";
      brazierAbilityId: string;
      acidAbilityId: string;
      mixerAbilityId: string;
      acidDamageMultiplierInBrazier: number;
      mixerBonusDamage: number;
    }
  | {
      kind: "debt_outsourcing";
      crowAbilityId: string;
      maxReductionPerRoundMs: number;
      reductionPerKillMs: number;
      successDoubleDurationMs: number;
    }
  | {
      kind: "night_shift_handoff";
      payoutMultiplier: number;
    }
  | {
      kind: "overmaintenance";
      jamEveryBasicAttacks: number;
      jamDurationMs: number;
    }
  | {
      kind: "needle_heal_counter";
      abilityIds: string[];
      hitsRequired: number;
      healAbilityId: string;
      healing: number;
    }
  | {
      kind: "dual_extremes_nth";
      abilityIds: string[];
      every: number;
    }
  | {
      kind: "echo_transfer";
      abilityIds: string[];
      damageRatio: number;
      target: "nearest_other";
    }
  | {
      kind: "stacking_armor_break";
      abilityIds: string[];
      maxReduction: number;
      durationMs: number;
      refreshAllOnHit: true;
    }
  | {
      kind: "guard_coordination";
      abilityIds: string[];
      armorAdd: number;
      attackSpeedBonus: number;
    }
  | {
      kind: "summon_death_cooldown";
      abilityIds: string[];
      reductionMs: number;
    }
  | {
      kind: "follower_trio_combo";
      abilityIds: string[];
      windowMs: number;
      damage: number;
    }
  | {
      kind: "stored_experience_companion_damage";
      abilityIds: string[];
      experiencePerStep: number;
      damageBonusPerStep: number;
    }
  | {
      kind: "pain_enjoyment";
      growthAbilityId: string;
      blockedDamageCounts: true;
      healPerSettlement: number;
    }
  | {
      kind: "bitter_medicine";
      healingAbilityIds: string[];
      shieldDurationMs: number;
      shieldMaxHealthRatio: number;
    }
  | {
      kind: "paper_aircraft_carrier";
      hiveAbilityId: string;
      kiteAbilityId: string;
      whaleAbilityId: string;
    }
  | {
      kind: "distant_target_bonus";
      abilityIds: string[];
      rangeAdd: number;
      distanceThreshold: number;
      damageMultiplier: number;
    }
  | {
      kind: "thunderstorm_counter";
      abilityIds: string[];
      hitsRequired: number;
      radius: number;
      damage: number;
    }
  | {
      kind: "freeze_combo";
      abilityIds: string[];
      maxSlowRatio: number;
      freezeDurationMs: number;
      freezeDamage: number;
    }
  | {
      kind: "soul_weighing";
      echoAbilityId: string;
      boomerangAbilityId: string;
      balanceAbilityId: string;
      echoDamageMultiplier: number;
      boomerangInitialDamageAdd: number;
      balanceEchoRatio: number;
    }
  | {
      kind: "hot_floor";
      abilityIds: string[];
      rangeAdd: number;
      triggerWindowMs: number;
      damageMultiplier: number;
      empoweredDurationMs: number;
    }
  | {
      kind: "guard_council";
      abilityIds: string[];
      maxSources: number;
      healthBonusPerSource: number;
      damageBonusPerSource: number;
    }
  | {
      kind: "undead_guard";
      targetAbilityId: string;
      respawnSummonUnitId: UndeadUnitId;
    }
  | {
      kind: "nest_riot";
      abilityIds: string[];
      radius: number;
      attackSpeedBonusPerOther: number;
      maxAttackSpeedBonus: number;
    }
  | {
      kind: "slacking_off";
      intervalMs: number;
      stationaryRadius: number;
      repair: number;
      maxPayoutsPerRepair: number;
    }
  | {
      kind: "spin_it";
      gearAbilityId: string;
      triggerReduction: number;
      armorReduction: number;
      armorDurationMs: number;
    }
  | {
      kind: "suppress_modules";
      moduleIds: string[];
    }
  | {
      kind: "max_health_regeneration";
      ratioPerSecond: number;
    }
  | {
      kind: "long_range_strike";
      abilityIds: string[];
      distanceThreshold: number;
      damageMultiplier: number;
      killEchoRatio: number;
    }
  | {
      kind: "thunder_god_indulgence";
      abilityIds: string[];
      countMultiplier: number;
      triggerCount: number;
      triggeredDamage: number;
    }
  | {
      kind: "absolute_domain";
      sourceAbilityIds: string[];
      chainRadius: number;
      freezeDurationMs: number;
      bossSlowRatio: number;
      bossSlowDurationMs: number;
      chainCanRecurse: false;
    }
  | {
      kind: "fire_land";
      sourceAbilityIds: string[];
      durationMs: number;
      radius: number;
      tickIntervalMs: number;
      damage: number;
      stackAtSamePosition: true;
    }
  | {
      kind: "blood_debt";
      healthCostAbilityId: string;
      empoweredAbilityId: string;
      lifestealAbilityId: string;
      damageMultiplier: number;
      durationMs: number;
      lifestealMultiplier: number;
    }
  | {
      kind: "the_world";
      sourceAbilityIds: string[];
      cooldownReductionMs: number;
      radius: number;
      pauseDurationMs: number;
      pauseBosses: false;
    }
  | {
      kind: "greed_and_wealth";
      suppressedModuleIds: string[];
      cycleMs: number;
      minRadius: number;
      maxRadius: number;
      tickIntervalMs: number;
      blackMinRadiusDamage: number;
      blackMaxRadiusDamage: number;
      whiteMinRadiusDamage: number;
      whiteMaxRadiusDamage: number;
      whiteHealthThresholdInclusive: number;
      whiteKnockbackDistance: number;
      heavyIntervalMs: number;
      heavyDamage: number;
      blackHeavyRange: number;
      whiteHeavyRange: number;
      whiteHeavyKnockbackOutsideRadius: number;
    }
  | {
      kind: "dual_gods_agreement";
      contractAbilityIds: string[];
      randomCastAbilityIds: string[];
      durationAddMs: number;
    };

export interface SynergyDefinition {
  id: string;
  name: string;
  description: string;
  requirements: SynergyRequirement[];
  bonuses: StatBonuses;
  abilityModifiers?: SynergyAbilityModifier[];
  mechanics?: SynergyMechanicDefinition[];
  special?: SpecialBehaviorId;
  apex?: boolean;
}

export interface SynergyAbilityModifier {
  moduleIds: string[];
  damageMultiplier?: number;
  intervalMultiplier?: number;
  rangeMultiplier?: number;
  radiusAdd?: number;
  projectilesAdd?: number;
  extraTargetsAdd?: number;
  manaCostMultiplier?: number;
  companionCountAdd?: number;
  companionHealthMultiplier?: number;
  companionSpawnIntervalMultiplier?: number;
  cannonBaseDiameterMultiplier?: number;
}

export interface AbilityTuning {
  damageMultiplier: number;
  intervalMultiplier: number;
  rangeMultiplier: number;
  radiusAdd: number;
  projectilesAdd: number;
  extraTargetsAdd: number;
  manaCostMultiplier: number;
  companionCountAdd: number;
  companionHealthMultiplier: number;
  companionSpawnIntervalMultiplier: number;
  cannonBaseDiameterMultiplier: number;
}

export interface TeamTechLevelDefinition {
  cost: number;
  effect: string;
}

export interface TeamTechDefinition {
  id: TeamTechId;
  name: string;
  description: string;
  levels: TeamTechLevelDefinition[];
}

export interface TeamTechState {
  id: TeamTechId;
  level: number;
  maxLevel: number;
  nextCost: number | null;
}

export interface TalentState {
  id: TalentId;
  name: string;
  description: string;
  effect: string;
  level: number;
  maxLevel: number;
  nextCost: number | null;
}

export interface EnemyDefinition {
  id: string;
  name: string;
  rank: EnemyRank;
  color: number;
  radius: number;
  health: number;
  armor?: number;
  speed: number;
  coreDamage: number;
  attackIntervalMs: number;
  scrapReward: number;
  experienceReward: number;
  coreReward: number;
  unlockAtMs: number;
  weight: number;
  /**
   * A rare, stronger member of the normal-rank army. It remains a normal
   * enemy for targeting and reward rules, but receives the explicit authored
   * stat increase and a 1.2x presentation scale.
   */
  normalStyle?: "special";
  specialBasedOnEnemyId?: string;
  attributeMultiplier?: number;
  splitInto?: string;
  ability?:
    | "slow"
    | "drain_mana"
    | "shield_aura"
    | "charge"
    | "spawn_minions"
    | "siege_marshal";
  /** Objective-only enemies never enter the ordinary timed spawn pool. */
  spawnSource?: "wave" | "objective";
}

export interface DifficultyDefinition {
  id: DifficultyId;
  name: string;
  enemyHealthMultiplier: number;
  enemyDamageMultiplier: number;
  spawnMultiplier: number;
  rewardMultiplier: number;
  eliteMultiplier: number;
  bossHealthMultiplier: number;
  /** @deprecated Difficulty no longer changes mining yields. */
  archaeologyMultiplier: number;
}

export interface PlayerStats {
  damage: number;
  damageMultiplier: number;
  attackDamageMultiplier: number;
  abilityDamageMultiplier: number;
  baseAttackSpeedModifier: number;
  attackSpeedBonus: number;
  fireIntervalMs: number;
  range: number;
  moveSpeed: number;
  moveSpeedMultiplier: number;
  maxHealth: number;
  armor: number;
  dodge: number;
  goldMultiplier: number;
  scrapMultiplier: number;
  experienceMultiplier: number;
  repairPerSecond: number;
  healingPerSecond: number;
  healthRegen: number;
  healthRegenMaxRatio: number;
  manaMax: number;
  manaRegen: number;
  manaRegenMaxRatio: number;
  scoutingRadius: number;
  splashRadius: number;
  chainTargets: number;
  pierceTargets: number;
  crystalDropChance: number;
  coreDropChance: number;
  eliteDamageMultiplier: number;
  companionDamageMultiplier: number;
  summonCap: number;
  /** General anti-invisibility radius retained from the pre-mining build. */
  revealRadius: number;
  surfaceMiningIntervalMultiplier: number;
  surfaceMiningExtraGoldFlat: number;
  surfaceMiningExtraGoldMultiplier: number;
  surfaceMiningSpeedBonus: number;
  surfaceMiningGoldYieldBonus: number;
  surfaceMiningRevealRadiusBonus: number;
  surfaceMiningCrystalYieldRatio: number;
  surfaceMiningManaSpeedBonus: number;
  surfaceMiningManaCostPerSettlement: number;
  /** @deprecated Archived archaeology compatibility only. */
  archaeologyMultiplier: number;
  /** @deprecated Archived archaeology compatibility only. */
  wageShare: number;
}

export interface PlayerPublicState {
  id: string;
  name: string;
  spaceId: WorldSpaceId;
  x: number;
  y: number;
  health: number;
  maxHealth: number;
  mana: number;
  maxMana: number;
  experience: number;
  nextLevelExperience: number;
  level: number;
  levelCap: number;
  talentPoints: number;
  talentPointCap: number;
  talents: TalentState[];
  wage: number;
  /** @deprecated Always zero in new games. */
  archaeologyYield: number;
  healthRegen: number;
  ready: boolean;
  connected: boolean;
  aiControlled: boolean;
  inputSequence: number;
  scrap: number;
  core: number;
  kills: number;
  status: PlayerStatus;
  reviveProgressMs: number;
  reviveRequiredMs: number;
  respawnAtMs: number;
  contribution: PlayerContribution;
  modules: Array<string | null>;
  lockedSlots: boolean[];
  synergies: string[];
  hiddenEffects: HiddenEffectState[];
  hiddenSynergies: HiddenSynergyState[];
  repeatableHiddenEffects: RepeatableHiddenEffectState[];
  hiddenSynergyProgress: HiddenSynergyProgressState[];
  luckyStarCount: number;
  labor: LaborPublicState;
  moduleRuntimeStates: ModuleSlotRuntimeState[];
  combatStats: {
    damage: number;
    attackIntervalMs: number;
    moveSpeed: number;
    armor: number;
    healthRegen: number;
    manaRegen: number;
    abilityDamageMultiplier: number;
    baseAttackSpeedModifier: number;
    attackSpeedBonus: number;
    perpetualCoreStacks: number;
    turretMoveRatio: number;
    dayNightPhase?: "day" | "night";
    abilityDamageGrowth: Record<string, number>;
    revealRadius: number;
    scoutingRadius: number;
  };
  surfaceMiningState: {
    veinId: number | null;
    devourProgressMs: number;
    devourRequiredMs: number;
    devourCooldownRemainingMs: number;
    devourCount: number;
    conditionalEvolutionTargetId: string | null;
    slotLocked: boolean;
  };
}

export interface ModuleSlotRuntimeState {
  slot: number;
  moduleId: string;
  experienceStored?: number;
  experienceCapacity?: number;
  replacementExperiencePayout?: number;
  killCharge?: number;
  killChargeRequired?: number;
  financeStatus?:
    | "income"
    | "waiting_payment"
    | "countdown"
    | "drain"
    | "spend_accumulator"
    | "charge"
    | "claimed"
    | "replacement_reward";
  financeRemainingMs?: number;
  financeWaitingResource?: "金币" | "结晶";
  financeRequiredAmount?: number;
  financeCurrentAmount?: number;
  financeShortfall?: number;
  financeStakeName?: string;
  financeStakeAmount?: number;
  financeSuccessPercent?: number;
  financeProgress?: number;
  financeProgressTarget?: number;
  financeTickCost?: number;
  financeReplacementReward?: number;
  financeRewardClaimed?: boolean;
  laborStatus?: "distance" | "circle" | "stationary" | "route" | "kill_bonus";
  laborProgress?: number;
  laborProgressTarget?: number;
  laborSecondaryProgress?: number;
  laborSecondaryTarget?: number;
  laborRemainingMs?: number;
  laborMovingRemainingMs?: number;
  laborLabel?: string;
  castProgress?: number;
  castRequired?: number;
}

export interface LaborPublicState {
  movementDistance: number;
  movementDistanceTarget: number;
  circleCount: number;
  circleTarget: number;
  circleCandidateActive: boolean;
  circleCandidatePathLength: number;
  circleCandidateSpanX: number;
  circleCandidateSpanY: number;
  circleCandidateTurnDegrees: number;
  circleCandidateCloseDistance: number;
  stationaryMs: number;
  stationaryTargetMs: number;
  routeLegs: number;
  routeTarget: number;
  routeArmedLabel: string | null;
  barbecueBonusGold: number;
  barbecueBonusGoldTarget: number;
}

export interface PlayerContribution {
  damageDealt: number;
  damageBySource: DamageSourceEntry[];
  scrapEarned: number;
  experienceEarned: number;
  goldDonated: number;
  goldReceived: number;
  surfaceMiningGoldEarned: number;
  /** @deprecated Archived archaeology metric; always zero in new games. */
  archaeologyEarned: number;
  coreDonated: number;
  coreReceived: number;
  teamTechInvested: number;
  repairDone: number;
  healingDone: number;
  objectiveMs: number;
  revives: number;
  timesDowned: number;
  signalsSent: number;
  oneShotsTriggered: number;
  companionsSummoned: number;
  firstModuleAtMs: number | null;
  firstSynergyAtMs: number | null;
  firstTierFiveAtMs: number | null;
  firstAscensionAtMs: number | null;
}

export type PlaytestInputMethod = "keyboard" | "touch" | "gamepad";
export type PlaytestDeviceClass = "desktop" | "mid_android" | "other_mobile";

export interface PlaytestClientMetrics {
  tier: "low" | "balanced" | "high";
  fpsP5Latest: number;
  fpsP5Minimum: number;
  fpsP5Average: number;
  sampleCount: number;
  maxRenderedEnemies: number;
  inputMethod: PlaytestInputMethod;
  viewport: string;
  userAgent: string;
  touchPoints: number;
}

export interface PlaytestSurvey {
  immediateReplay: boolean;
  buildReadability: number;
  movementComfort: number;
  cooperationValue: number;
  failureClarity: number;
  deviceClass: PlaytestDeviceClass;
  panelIssue: boolean;
  backgroundReconnectPassed: boolean | null;
  notes: string;
}

export interface PlaytestSubmission {
  reportVersion: 1;
  testerId: string;
  roomCode: string;
  playerId: string;
  token: string;
  playerHostReceipt?: string;
  client: PlaytestClientMetrics;
  survey: PlaytestSurvey;
}

export interface EnemyWireState {
  id: number;
  definitionId: string;
  x: number;
  y: number;
  health: number;
  maxHealth: number;
  armor: number;
  spaceId: WorldSpaceId;
  velocityX: number;
  velocityY: number;
  stunnedUntilMs?: number;
}

export interface AbilityEntityWireState {
  id: number;
  ownerId: string;
  abilityId: string;
  kind: AbilityEntityKind;
  presentationId: string;
  spaceId: WorldSpaceId;
  x: number;
  y: number;
  health: number;
  maxHealth: number;
  ethereal?: boolean;
  effectRadius?: number;
  undeadUnitId?: UndeadUnitId;
  taunting?: boolean;
  guardForm?: GuardFormId;
  earthSpiritStar?: EarthSpiritStar;
  earthSpiritBehavior?: EarthSpiritBehavior;
}

export interface DefenseSegmentState {
  id: number;
  ring: 1 | 2 | 3;
  kind: "wall" | "gate";
  x: number;
  y: number;
  width: number;
  height: number;
  health: number;
  maxHealth: number;
  armor: number;
  hasTower: boolean;
  destroyed: boolean;
}

export type CityBuildingFunction = "none" | "producer";
export type CityAllyModelId = "g-a" | "g-b" | "g-c";
export type CityAllyBehavior = "rallying" | "active" | "returning";

/** Fixed monster-city structures. Positions and combat state are authoritative. */
export interface CityBuildingState {
  id: string;
  definitionId: string;
  name: string;
  presentationId: string;
  function: CityBuildingFunction;
  x: number;
  y: number;
  radius: number;
  health: number;
  maxHealth: number;
  armor: number;
  destroyed: boolean;
  spawnModelId?: CityAllyModelId;
  spawnIntervalMs?: number;
  spawnBatchSize?: number;
  activeUnitCount: number;
  globalUnitCap?: number;
}

/** Building-produced monster allies. They rally at a linked gate before fighting. */
export interface CityAllyUnitState {
  id: number;
  modelId: CityAllyModelId;
  presentationId: string;
  x: number;
  y: number;
  health: number;
  maxHealth: number;
}

export type SurfaceVeinSize = "small" | "medium" | "large";

/** Viewer-safe P4.13 state. Hidden vein coordinates are omitted entirely. */
export interface SurfaceVeinViewState {
  id: number;
  size: SurfaceVeinSize;
  x: number;
  y: number;
  radius: number;
  remaining: number;
  maxAmount: number;
}

export interface ArchaeologyNodeState {
  id: number;
  kind: "vein" | "chest";
  x: number;
  y: number;
  size: 1 | 2 | 3;
  color?: "crimson" | "azure" | "verdant" | "golden";
  depleted: boolean;
  revealedBy: string[];
}

export interface SideObjectiveState {
  id: SideObjectiveId;
  name: string;
  description: string;
  /** Stable presentation key; gameplay never branches on this value. */
  presentationId?: string;
  status: "waiting" | "active" | "completed" | "failed";
  x: number;
  y: number;
  activateAtMs: number;
  progressMs: number;
  requiredMs: number;
  expiresAtMs: number;
  health?: number;
  maxHealth?: number;
  invulnerable?: boolean;
  attractionRadius?: number;
  kills?: number;
  requiredKills?: number;
  spawnedCount?: number;
  routeGateId?: number;
  routePhase?: "to_gate" | "to_core";
  moveSpeed?: number;
  /** True only after a living player has physically entered the task area. */
  playerEngaged?: boolean;
  completedAtMs?: number;
}

export interface LaneState {
  id: LaneId;
  name: string;
  x: number;
  y: number;
  pressure: number;
  enemyCount: number;
  eliteCount: number;
}

export interface TeamSignalState {
  id: number;
  playerId: string;
  playerName: string;
  kind: TeamSignalKind;
  laneId: LaneId;
  x: number;
  y: number;
  createdAtMs: number;
  expiresAtMs: number;
}

export interface PerformanceMetrics {
  tickP95Ms: number;
  outgoingKbpsPerClient: number;
  enemyCount: number;
  abilityEntityCount: number;
}

export type PlayerMotionState = [
  id: string,
  x: number,
  y: number,
  health: number,
  mana: number,
  status: PlayerStatus,
  inputSequence: number,
  connected: boolean,
  aiControlled: boolean,
  reviveProgressMs: number,
  respawnAtMs: number,
  spaceId: WorldSpaceId,
];

export type EnemyAnchorState = [
  id: number,
  x: number,
  y: number,
  health: number,
  velocityX: number,
  velocityY: number,
];

export type AbilityEntityAnchorState = [
  id: number,
  x: number,
  y: number,
  health: number,
  spaceId: WorldSpaceId,
];

export type DefenseAnchorState = [
  id: number,
  health: number,
  maxHealth: number,
  armor: number,
  destroyed: boolean,
];

export type CityBuildingAnchorState = [
  id: string,
  health: number,
  destroyed: boolean,
  activeUnitCount: number,
];

export type CityAllyAnchorState = [
  id: number,
  x: number,
  y: number,
  health: number,
];

export interface PublicRoomState {
  protocolVersion: number;
  contentVersion: string;
  runSeed: string;
  roomCode: string;
  phase: RoomPhase;
  hostId: string;
  difficulty: DifficultyId;
  serverTime: number;
  elapsedMs: number;
  durationMs: number;
  core: {
    x: number;
    y: number;
    radius: number;
    health: number;
    maxHealth: number;
    armor: number;
  };
  players: PlayerPublicState[];
  playerMotions?: PlayerMotionState[];
  enemies?: EnemyWireState[];
  enemyAnchors?: EnemyAnchorState[];
  abilityEntities?: AbilityEntityWireState[];
  abilityEntityAnchors?: AbilityEntityAnchorState[];
  defenses: DefenseSegmentState[];
  defenseAnchors?: DefenseAnchorState[];
  cityBuildings: CityBuildingState[];
  cityBuildingAnchors?: CityBuildingAnchorState[];
  cityAllies?: CityAllyUnitState[];
  cityAllyAnchors?: CityAllyAnchorState[];
  defenseEconomy: {
    towerGoldPool: number;
    towerGoldPaid: number;
  };
  /** Dynamically cropped for the receiving player/team; never a discovery log. */
  surfaceVeins: SurfaceVeinViewState[];
  /** @deprecated Protocol-17 compatibility; new games always return an empty list. */
  archaeologyNodes: ArchaeologyNodeState[];
  lanes: LaneState[];
  objectives: SideObjectiveState[];
  victoryProgress: {
    completedObjectives: number;
    requiredObjectives: number;
    finalBossDefeated: boolean;
  };
  teamTech: TeamTechState[];
  signals: TeamSignalState[];
  metrics: PerformanceMetrics;
  resultReason?: string;
}

export type OfferMode = "upgrade" | "reforge";

export interface UpgradeOffer {
  id: string;
  mode: OfferMode;
  slot: number;
  currentModuleId: string | null;
  targetTier: ModuleTier;
  scrapCost: number;
  crystalCost: number;
  rerollCost: number;
  candidates: string[];
  candidateWeightMultipliers?: Record<string, number>;
  canAbandon: boolean;
  reusedCandidates: boolean;
}

export type HandshakeMessage =
  | {
      type: "create";
      name: string;
      seed?: string;
      protocolVersion: number;
      contentVersion: string;
    }
  | {
      type: "join";
      roomCode: string;
      name: string;
      protocolVersion: number;
      contentVersion: string;
    }
  | {
      type: "reconnect";
      roomCode: string;
      playerId: string;
      token: string;
      protocolVersion: number;
      contentVersion: string;
    };

export type ClientMessage =
  | HandshakeMessage
  | { type: "ready"; ready: boolean }
  | { type: "start"; difficulty: DifficultyId }
  | { type: "input"; sequence: number; x: number; y: number }
  | { type: "requestOffer"; slot: number }
  | { type: "requestReforge"; slot: number }
  | { type: "rerollOffer"; offerId: string }
  | { type: "cancelOffer"; offerId: string }
  | { type: "selectOffer"; offerId: string; moduleId: string }
  | { type: "ascendAbility"; slot: number }
  | { type: "investTalent"; talentId: TalentId }
  /** @deprecated Protocol-18 compatibility. Always rejected as archived. */
  | { type: "mineTransit"; caveId: MineCaveId; direction: "enter" | "exit" }
  /** @deprecated Protocol-18 compatibility. Always rejected as archived. */
  | { type: "mineWork"; targetKind: "rubble" | "deposit"; targetId: number; active: boolean }
  /** @deprecated Protocol-18 compatibility. Always rejected as archived. */
  | { type: "minePickup"; cargoId: number }
  /** @deprecated Protocol-18 compatibility. Always rejected as archived. */
  | { type: "mineSurvey"; caveId?: MineCaveId }
  /** @deprecated Protocol-17 compatibility. Always rejected by new games. */
  | { type: "excavate"; nodeId: number }
  | {
      type: "aidResource";
      targetPlayerId: string;
      resource: "gold" | "crystal";
      amount: number;
    }
  /** @deprecated Alias for crystal aid. */
  | { type: "donateCore"; targetPlayerId: string; amount: number }
  | { type: "investTeamTech"; techId: TeamTechId }
  | { type: "teamSignal"; kind: TeamSignalKind; laneId: LaneId }
  | { type: "ping"; clientTime: number };

export type SimulationEvent =
  | { type: "enemySpawn"; enemies: EnemyWireState[] }
  | { type: "enemyDeath"; enemyIds: number[] }
  | { type: "shot"; playerId: string; targetIds: number[]; abilityId?: string }
  | { type: "defenseShot"; sourceX: number; sourceY: number; targetId: number }
  | { type: "abilityEntities"; entities: AbilityEntityWireState[] }
  | {
      type: "surfaceMining";
      playerId: string;
      baseGold: number;
      bonusGold: number;
      totalGold: number;
      crystals: number;
      depleted: boolean;
    }
  | {
      type: "surfaceMiningDevour";
      playerId: string;
      totalGold: number;
      devourCount: number;
      evolved: boolean;
    }
  /** @deprecated Protocol-17 compatibility only. */
  | { type: "archaeology"; playerId: string; nodeId: number; gold: number; crystals: number }
  | { type: "teamSignal"; signal: TeamSignalState }
  | { type: "notice"; message: string }
  | {
      type: "result";
      outcome: "victory" | "draw" | "defeat";
      /** Compatibility for existing result consumers. */
      victory: boolean;
      reason: string;
    };

export type ServerMessage =
  | {
      type: "welcome";
      roomCode: string;
      playerId: string;
      token: string;
      resumed: boolean;
      state: PublicRoomState;
      offer?: UpgradeOffer;
    }
  | { type: "snapshot"; state: PublicRoomState }
  | { type: "offer"; offer: UpgradeOffer }
  | { type: "offerCleared" }
  | SimulationEvent
  | { type: "pong"; clientTime: number; serverTime: number }
  | { type: "error"; code: string; message: string };
